import { SelectItemV2, UNIT_DEFAULTS } from '@schenck/rotec-components';
import { CorrectionMethod, DisplayInBalancing } from 'src/utils/enums/rotor-type.enum';
import { DrillTip, RotorTypeValueV2 } from 'src/utils/interfaces/rotor-type.interfaces';

export const CorrectionMethodStepImages = {
  left: {
    addMass: './assets/img/correction-method/Left-Plane/Add-Mass.png',
    drillAxialFromLeft: './assets/img/correction-method/Left-Plane/Drill-Axial-from-left.png',
    drillAxialFromRight: './assets/img/correction-method/Left-Plane/Drill-Axial-from-right.png',
    drillRadial: './assets/img/correction-method/Left-Plane/Drill-Radial.png',
    noCorrection: './assets/img/correction-method/Left-Plane/NoCorrection.png',
    removedCorrection: './assets/img/correction-method/Left-Plane/RemovedCorrection.png',
    removeMass: './assets/img/correction-method/Left-Plane/Remove-Mass.png',
    weightSet: './assets/img/correction-method/Left-Plane/Weights.png',
  },
  right: {
    addMass: './assets/img/correction-method/Right-Plane/Add-Mass.png',
    drillAxialFromLeft: './assets/img/correction-method/Right-Plane/Drill-Axial-from-left.png',
    drillAxialFromRight: './assets/img/correction-method/Right-Plane/Drill-Axial-from-right.png',
    drillRadial: './assets/img/correction-method/Right-Plane/Drill-Radial.png',
    noCorrection: './assets/img/correction-method/Right-Plane/NoCorrection.png',
    removedCorrection: './assets/img/correction-method/Right-Plane/RemovedCorrection.png',
    removeMass: './assets/img/correction-method/Right-Plane/Remove-Mass.png',
    weightSet: './assets/img/correction-method/Right-Plane/Weights.png',
  },
  static: {
    // for now seems unsopported
    addMass: './assets/img/correction-method/Static/Add-Mass.png',
    drillAxialFromLeft: './assets/img/correction-method/Static/Drill-Axial-from-left.png',
    drillAxialFromRight: './assets/img/correction-method/Static/Drill-Axial-from-right.png',
    drillRadial: './assets/img/correction-method/Static/Drill-Radial.png',
    removeMass: './assets/img/correction-method/Static/Remove-Mass.png',
    weightSet: './assets/img/correction-method/Static/Weights.png',
  },
  functions: {
    getSettingsDescription: function (value: number) {
      if (!value) return '';

      switch (value) {
        case CorrectionMethod.AddMass:
          return 'Add Mass';
        case CorrectionMethod.RemoveMass:
          return 'Remove Mass';
        case CorrectionMethod.Drill:
          return 'Drill';
        case CorrectionMethod.WeightSets:
          return 'Weights';
      }
    },
    getImage: function (
      right: boolean,
      setting: number,
      isStatic: boolean,
      drillSelectionValue: number
    ) {
      if (isStatic && right) {
        console.error('static should not contain right side');
        return;
      }

      let part = right ? 'right' : 'left';
      let settingProp: string;

      if (isStatic) {
        part = 'static';
      }

      switch (setting) {
        case CorrectionMethod.AddMass:
          settingProp = 'addMass';
          break;
        case CorrectionMethod.RemoveMass:
          settingProp = 'removeMass';
          break;
        case CorrectionMethod.WeightSets:
          settingProp = 'weightSet';
          break;
        default:
          settingProp = 'addMass';
          break;
      }

      switch (setting === CorrectionMethod.Drill && drillSelectionValue) {
        case 1:
          settingProp = 'drillAxialFromRight';
          break;
        case 2:
          settingProp = 'drillAxialFromLeft';
          break;
        case 3:
          settingProp = 'drillRadial';
          break;
      }

      return CorrectionMethodStepImages[part][settingProp];
    },
  },
};

export const CORRECTION_METHODS = ['Add Mass', 'Remove Mass', 'Drill', 'Weights'];

export const DRILL_DIRECTION_OPTIONS = [
  {
    value: 1,
    name: 'Drill Axial from Right',
  },
  {
    value: 2,
    name: 'Drill Axial from Left',
  },
  {
    value: 3,
    name: 'Radial',
  },
];

export const CORRECTION_DIRECTIONS: RotorTypeValueV2[] = [
  { name: 'Drill Axial from Right', value: 1, isEdited: true },
  { name: 'Drill Axial from Left', value: 1, isEdited: true },
  { name: 'Radial', value: 0, isEdited: true },
];

export const RADIAL_CORRECTION_ORIENTATIONS = [
  { name: 'Drill Axial from Right', value: 3 },
  { name: 'Drill Axial from Left', value: 3 },
  { name: 'Radial', value: 1 },
];

export const AXIAL_CORRECTION_ORIENTATIONS = [
  { name: 'Drill Axial from Right', value: 1 },
  { name: 'Drill Axial from Left', value: 2 },
  { name: 'Radial', value: 3 },
];

export const CIRCULAR_CORRECTION_ORIENTATIONS = [
  { name: 'Drill Axial from Right', value: 3 },
  { name: 'Drill Axial from Left', value: 3 },
  { name: 'Radial', value: 3 },
];

export const CORRECTION_DRILL_TIPS = ['Standard 118°', 'General 113°', 'Cylinder', 'Custom'];

export const CorrectionDrillTipsTypes: DrillTip[] = [
  {
    name: 'RotorManager.Domain.Model.RotorTypeManagement.V1.StandardDrillTip',
    value: 118,
    isEdited: true,
    crossEdgeFactor: 0.15,
    type: 'TruncatedConeStandard118And015',
    drillTipAngle: { type: 'StandardDrillTipAngle', value: 118, isEdited: true },
  },
  {
    name: 'RotorManager.Domain.Model.RotorTypeManagement.V1.StandardDrillTip',
    value: 113,
    isEdited: true,
    crossEdgeFactor: 0.15,
    type: 'TruncatedConeStandard113And015',
    drillTipAngle: { type: 'GeneralDrillTipAngle', value: 113, isEdited: true },
  },
  {
    name: 'RotorManager.Domain.Model.RotorTypeManagement.V1.CylinderDrillTip',
    value: null,
    type: 'Cylinder',
    isEdited: true,
  },
  {
    name: 'RotorManager.Domain.Model.RotorTypeManagement.V1.StandardDrillTip',
    type: 'TruncatedConeGeneral',
    drillTipAngle: { type: 'CustomDrillTipAngle' },
    isEdited: true,
  },
];

export const MATERIAL_DENSITY_LIST: SelectItemV2<number>[] = [
  {
    value: 1,
    name: 'Brass',
    subname: 8.46,
    unit: UNIT_DEFAULTS.density,
  },
  {
    value: 2,
    name: 'Ductile Cast Iron',
    subname: 7.1,
    unit: UNIT_DEFAULTS.density,
  },
  {
    value: 3,
    name: 'Steel C45',
    subname: 7.87,
    unit: UNIT_DEFAULTS.density,
  },
  {
    value: 4,
    name: 'Steel ST235',
    subname: 7.85,
    unit: UNIT_DEFAULTS.density,
  },
  {
    value: 5,
    name: 'Steel 42CrMo',
    subname: 7.82,
    unit: UNIT_DEFAULTS.density,
  },
  {
    value: 6,
    name: 'Aluminium',
    subname: 2.7,
    unit: UNIT_DEFAULTS.density,
  },
];

export enum MaterialDensityEnum {
  'Custom',
  'Brass',
  'Ductile Cast Iron',
  'Steel C45',
  'Steel ST235',
  'Steel 42CrMo',
  'Aluminium',
}

export const CORRECTION_SETTINGS_METHODS = [
  { value: CorrectionMethod.AddMass, icon: 'weight', name: 'Mass' },
  { value: CorrectionMethod.WeightSets, icon: 'weight-sets', name: 'Weights' },
  { value: CorrectionMethod.Drill, icon: 'correction-drill', name: 'Drill' },
];
export const CORRECTION_MASS_METHODS = [
  { value: CorrectionMethod.AddMass, icon: 'correction-add-weight', name: 'Add Mass' },
  { value: CorrectionMethod.RemoveMass, icon: 'correction-remove-weight', name: 'Remove Mass' },
];
export const DRILL_TIP_TILES = [
  {
    value: 1,
    name: 'Standard 118°',
    image: './assets/img/correction-method/drill-tip/Standard.png',
  },
  {
    value: 2,
    name: 'General 113°',
    image: './assets/img/correction-method/drill-tip/General.png',
  },
  {
    value: 3,
    name: 'Cylinder',
    image: './assets/img/correction-method/drill-tip/Cylinder.png',
  },
  {
    value: 4,
    name: 'Custom',
  },
];

export const DISPLAY_IN_BALANCING: SelectItemV2<DisplayInBalancing>[] = [
  { name: 'Weight Number', value: DisplayInBalancing.WeightNumber },
  { name: 'Weight Name', value: DisplayInBalancing.WeightName },
  { name: 'Weight Name and Number', value: DisplayInBalancing.WeightNameAndNumber },
];
